import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleContent, relatedArticles } from '@/mocks/articleData';

const articleSchema = (article: typeof articleContent[string]) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  author: {
    '@type': 'Organization',
    name: article.author,
  },
  datePublished: article.date,
  image: article.coverImage,
  articleBody: article.content.map(c => c.text).join(' '),
  publisher: {
    '@type': 'Organization',
    name: '向日葵视频',
    url: 'https://www.xiangrikui.com',
  },
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const article = slug ? articleContent[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2 font-heading">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors no-underline cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema(article))}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Article Header */}
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '热门文章', href: '/category/all' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <h1 className="text-xl md:text-3xl font-bold text-foreground-900 mt-3 font-heading leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views} 次阅读
                </span>
                <Link
                  to={`/category/${article.category}`}
                  className="whitespace-nowrap px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs no-underline hover:bg-accent-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
              </div>
            </div>
          </section>

          {/* Cover Image */}
          <section className="max-w-[900px] mx-auto px-4 md:px-6 -mt-6">
            <div className="rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>
          </section>

          {/* Article Content */}
          <article className="max-w-[780px] mx-auto px-4 md:px-6 py-10">
            <div className="prose-custom">
              {article.content.map((block, index) => {
                if (block.type === 'h2') {
                  return (
                    <h2 key={index} className="text-xl font-bold text-foreground-900 mt-10 mb-4 font-heading">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-base text-foreground-700 leading-relaxed mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="whitespace-nowrap px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="py-14 md:py-20 bg-background-100" aria-label="相关推荐">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 font-heading">相关文章推荐</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    category="banana"
                    coverImage="https://readdy.ai/api/search-image?query=Colorful%20fresh%20fruits%20in%20wooden%20baskets%20on%20a%20sunlit%20kitchen%20counter%2C%20warm%20natural%20light%2C%20rustic%20style%2C%20food%20photography%2C%20cozy%20atmosphere%2C%20vibrant%20colors&width=800&height=500&seq=rel-article-01&orientation=landscape"
                    excerpt={ra.excerpt}
                    date={ra.date}
                    readTime={ra.readTime}
                    views="3.2万"
                  />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}